import { createAjv } from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Button, Modal } from "pepsico-ds";
import { useContext, useState } from "react";
import { ActivitySetupContext } from "../../../../context/ActivitySetupContext";
import { CampaignSetupContext } from "../../../../context/CampaignSetupContext";
import { useToast } from "../../../../hooks/useToast";
import ViewComponentsRenderer from "../../../campaign/viewComponents/ViewComponentsRenderer";
import ButtonGroupDynamic from "../../../common/ButtonGroupDynamic";
import ToastNotification from "../../../common/toastNotification/ToastNotification";
import { ActivitySetupFormWrapper } from "../../activitySetupFormsWrapper/ActivitySetupFormsWrapper";
import "./default-rule-setup.scss";

const DefaultRuleSetupComponent = ({ data, path, handleChange, uischema }) => {
  // Use isNew flag from uischema to determine if modal should open
  const isNewRule = uischema.isNew === true;
  const [currentData, setCurrentData] = useState(isNewRule ? null : data);
  const [showModal, setShowModal] = useState(isNewRule);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const { currentCampaignTypeConfig } = useContext(CampaignSetupContext);
  const { activity, removeRule } = useContext(ActivitySetupContext);
  const { toast, showToast, closeToast } = useToast();
  const jsonFormsState = useJsonForms();
  const ajv = createAjv();
  const coreSchema = jsonFormsState.core.schema;
  ajv.validate(coreSchema, data);
  const errors = ajv.errors || [];
  const toggleModal = () => setShowModal(!showModal);
  const toggleRemoveRuleConfirmation = () =>
    setShowRemoveConfirmation(!showRemoveConfirmation);

  // Helper to detect multiStepController in UI schema
  const hasMultiStepController = (uiSchema) => {
    if (!uiSchema) return false;

    const checkElement = (element) => {
      if (element?.component === "multiStepController") return true;
      if (element?.elements && Array.isArray(element.elements)) {
        return element.elements.some(checkElement);
      }
      if (element?.options?.steps && Array.isArray(element.options.steps)) {
        return true;
      }
      return false;
    };

    if (uiSchema.component === "multiStepController") return true;
    if (uiSchema.elements && Array.isArray(uiSchema.elements)) {
      return uiSchema.elements.some(checkElement);
    }
    return false;
  };

  const onCancel = () => {
    if (!currentData) {
      removeRule(uischema.ruleSlug, uischema.occurrence);
      toggleModal();
      return;
    }
    handleChange(path, currentData);
    toggleModal();
  };

  const onSave = () => {
    setCurrentData(data);
    toggleModal();
    showToast(
      "success",
      `${uischema.label} successfully added to the activity.`,
      ""
    );
  };

  const getIsRequired = () => {
    const rulesConfiguration =
      activity.activityDefinition.rulesConfiguration ||
      currentCampaignTypeConfig.requirement.possibleActivities.find(
        (a) => a.id === activity.activityDefinition.id
      ).rulesConfiguration;

    const isRequiredConfiguration =
      rulesConfiguration?.rules.find((rule) => rule.rule?.slug === uischema.ruleSlug)
        ?.minRequired > 0;

    const timesAdded = activity.activityRules.filter(
      (rule) => rule.ruleDefinition.slug === uischema.ruleSlug
    ).length;

    return (
      (isRequiredConfiguration ||
        activity.activityDefinition.requiredRules?.includes(uischema.ruleSlug)) &&
      timesAdded
    );
  };

  const isRequired = getIsRequired();
  const childUiSchema = uischema.options.detail;
  const isMultiStep = hasMultiStepController(childUiSchema);

  return (
    <div className="default-rule-setup">
      {currentData && (
        <div className="rule-details">
          <ViewComponentsRenderer
            inputSchema={coreSchema}
            uiSchema={childUiSchema}
            data={currentData}
            customComponentName={uischema.ruleSlug}
          />
          <ButtonGroupDynamic
            size="small"
            items={[
              {
                icon: "edit",
                onClick: toggleModal,
              },
              {
                icon: "delete",
                disabled: isRequired,
                onClick: toggleRemoveRuleConfirmation,
              },
            ]}
          />
        </div>
      )}
      <Dialog
        key="default-rule-setup-modal"
        open={showModal}
        onClose={onCancel}
        PaperProps={{ className: "default-rule-setup-modal-paper" }}
        scroll="paper"
      >
        <DialogTitle>
          <div className="modal-header">
            <div className="font-heading-h5-bold">{uischema.label}</div>
            <CloseIcon onClick={onCancel} className="cursor-pointer" />
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="content-body">
            <ActivitySetupFormWrapper
              jsonschema={coreSchema}
              uischema={childUiSchema}
              data={data}
              onChange={(data) => handleChange(path, data)}
              config={{
                onCancel: onCancel,
                onSave: onSave,
              }}
            />
          </div>
        </DialogContent>
        {!isMultiStep && (
          <div className="modal-footer">
            <Button variant="tertiary" size="medium" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="medium"
              disabled={errors.length}
              onClick={onSave}
            >
              Save
            </Button>
          </div>
        )}
      </Dialog>
      <Modal
        className="remove-rule-confirmation"
        showModal={showRemoveConfirmation}
        onCloseModal={toggleRemoveRuleConfirmation}
        title="Are you sure you want to remove this rule?"
        primaryButtonProps={{
          size: "medium",
          text: "Remove",
          variant: "primary",
          onClick: () => {
            toggleRemoveRuleConfirmation();
            removeRule(uischema.ruleSlug, uischema.occurrence);
          },
        }}
        secondaryButtonProps={{
          text: "Cancel",
          size: "medium",
          variant: "tertiary",
          onClick: toggleRemoveRuleConfirmation,
        }}
        showSecondaryButton={true}
        showTertiaryButton={false}
        showLink={false}
      >
        <span className="w-100">This action cannot be undone.</span>
      </Modal>
      <ToastNotification {...toast} handleClose={closeToast} />
    </div>
  );
};

export const DefaultRuleSetupControl = withJsonFormsControlProps(
  DefaultRuleSetupComponent
);
